import AvisoRotacionarTela from '@/global/components/AvisoRotacionarTela'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import {
    CreateTrocaInicioPontoSchema,
    empresas as empresa_api,
    TrocaInicioPonto,
    trocaInicioPonto,
} from '@/service/api'
import { zod } from '@acme/core'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import FormAlterarInicioPonto from '../atoms/forms/AlterarInicioPonto/FormAlterarInicioPonto'
import { useEditarEmpresaOutletContext } from './Editar'

const AlterarInicioPontoSchema = CreateTrocaInicioPontoSchema.omit({
    empresaId: true,
}).extend({
    _opcao: z.enum(['registrar', 'corrigir']),
})

type AlterarInicioPontoValues = z.infer<typeof AlterarInicioPontoSchema>

export default function AlterarFechamentoPonto() {
    const { data: empresa } = useEditarEmpresaOutletContext()
    const { data: trocas = [], isFetched } = trocaInicioPonto.useFetch({
        queryParams: { empresaId: empresa.id },
        enabled: !!empresa.id,
    })
    const navigate = useNavigate()
    const trocaExistente = trocas.find(
        it => it.competencia === empresa.competenciaAtual,
    )
    const defaultValues =
        trocaExistente ||
        ({
            diaInicioPonto: empresa.diaInicioPonto,
            diaAnterior: (() => {
                const proximaTroca = trocas.find(it =>
                    dayjs(it.competencia, 'YYYY-MM').isAfter(
                        dayjs(empresa.competenciaAtual, 'YYYY-MM'),
                        'month',
                    ),
                )
                if (proximaTroca) return proximaTroca.diaAnterior
                return empresa.diaInicioPonto
            })(),
        } as TrocaInicioPonto)
    const { mutateAsync, isLoading } = trocaInicioPonto.mutations.useCreate({
        onSuccess: () => {
            toast.success('Troca de início de ponto registrada com sucesso')
        },
        onError: error => {
            toast.error(error.message)
        },
    })
    const { mutateAsync: changeDia, isLoading: isChangingDia } =
        trocaInicioPonto.dia.mutations.useUpdate({
            onSuccess: () => {
                toast.success('Dia de início do ponto alterado com sucesso')
                empresa_api.invalidateQueries()
                navigate('/cadastros/empresas')
            },
            onError: error => {
                toast.error(error.message)
            },
        })

    const [salvarDesabilitado, setSalvarDesabilitado] = useState(false)
    const [confirmacao, setConfirmacao] =
        useState<AlterarInicioPontoValues | null>(null)

    if (!empresa || !isFetched) return null

    return (
        <>
            <Dialog.Root
                loading={isLoading || isChangingDia}
                open={hasPermission(['update', 'Empresas'])}
                schema={AlterarInicioPontoSchema.or(
                    zod.EmpresaSchema.pick({ diaInicioPonto: true }),
                )}
                defaultValues={{
                    diaInicioPonto: defaultValues.diaInicioPonto,
                    diaAnterior: defaultValues.diaAnterior,
                    competencia: empresa.competenciaAtual,
                    _opcao: 'corrigir',
                }}
                onSubmit={async values => {
                    if (values._opcao === 'registrar') {
                        setConfirmacao(values)
                    } else {
                        await changeDia({
                            id: empresa.id,
                            diaInicioPonto: values.diaInicioPonto,
                        })
                    }
                }}
                width="5xl"
            >
                <Dialog.Title action="Alterar dia de início do ponto">
                    {empresa.nomeFantasia}
                </Dialog.Title>
                <Dialog.Content>
                    <div className="sm:hidden">
                        <AvisoRotacionarTela />
                    </div>
                    <FormAlterarInicioPonto
                        trocasInicioPonto={trocas}
                        onValidityChange={valido =>
                            setSalvarDesabilitado(!valido)
                        }
                    />
                </Dialog.Content>
                <Dialog.Actions>
                    <Dialog.Actions.SecondaryButton />
                    <Dialog.Actions.PrimaryButton
                        color="warning"
                        disabled={salvarDesabilitado}
                    >
                        Registrar alteração
                    </Dialog.Actions.PrimaryButton>
                </Dialog.Actions>
            </Dialog.Root>

            <Dialog.Root
                loading={isLoading}
                open={!!confirmacao}
                onClose={() => setConfirmacao(null)}
                onSubmit={async () => {
                    if (!confirmacao) return
                    await mutateAsync({
                        empresaId: empresa.id,
                        ...confirmacao,
                    })
                    empresa_api.invalidateQueries()
                    navigate('/cadastros/empresas')
                }}
            >
                <Dialog.Title>Confirmar alteração</Dialog.Title>
                <Dialog.Content>
                    Tem certeza que deseja prosseguir com a alteração do dia de
                    início do ponto?
                </Dialog.Content>
                <Dialog.Actions>
                    <Dialog.Actions.SecondaryButton />
                    <Dialog.Actions.PrimaryButton color="warning">
                        Confirmar
                    </Dialog.Actions.PrimaryButton>
                </Dialog.Actions>
            </Dialog.Root>
        </>
    )
}
